import React, { useEffect, useRef } from "react";
import lottie from "lottie-web";

// SCRUM-127: animated sign-in hero. The Lottie (growth_tree-v2.json) is served
// from /public so it is fetched lazily by lottie-web (kept out of the JS bundle),
// the same way the old static sign-in-tree.svg was served. The filename is
// versioned because deployed assets sit behind long-lived CDN caches with no
// invalidation step , a new URL is the only reliable way to ship a new asset.
const ANIM_PATH = "/assets/animations/growth_tree-v2.json";

// Authored markers: "intro" = frames 0-92 (grow-in), "loop" = frames 92-614
// (the idle "lights travel the veins + glow the logos" loop).
const LOOP_START = 92;
const LOOP_END = 614;

const HeroTree = ({ className }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    // Product decision (SCRUM-127): the brand hero must ALWAYS animate for every
    // viewer on every browser/machine, so we intentionally do NOT gate on
    // prefers-reduced-motion (that OS setting was suppressing it for any user who
    // had reduced motion enabled). Proven base: loop + autoplay animates reliably
    // (the per-segment "complete" event is NOT reliable on this asset, which froze
    // earlier versions). We loop the FULL clip, then once the grow-in has played we
    // restrict the loop to the idle segment so the tree never re-grows. If that
    // restriction ever fails, it simply keeps looping the full clip -- it can never
    // get stuck.
    const anim = lottie.loadAnimation({
      container,
      renderer: "svg",
      loop: true,
      autoplay: true,
      path: ANIM_PATH,
      rendererSettings: { preserveAspectRatio: "xMidYMid meet" },
    });

    let switched = false;
    const onFrame = () => {
      if (switched) return;
      // currentFrame is absolute during the initial full play (0 -> 614).
      if (anim.currentFrame >= LOOP_START) {
        switched = true;
        anim.removeEventListener("enterFrame", onFrame);
        // Loop only the idle segment from now on (loop is already true).
        anim.playSegments([LOOP_START, LOOP_END], true);
      }
    };
    anim.addEventListener("enterFrame", onFrame);

    return () => {
      anim.removeEventListener("enterFrame", onFrame);
      anim.destroy();
    };
  }, []);

  return <div ref={containerRef} className={className} aria-hidden="true" />;
};

export default HeroTree;
